import {
  Banner,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  RadioGroup,
  ToastUtils,
  Typography,
} from '@forgeglobal/design-framework';
import { useOktaAuth } from '@okta/okta-react';
import { AssetValuationDetails } from '@tcs/client-portal-api/types/AssetValuationDetails';
import { ValuationFormData } from '@tcs/client-portal-api/types/ValuationFormData';
import valuationChangeRequiresVerification from '@tcs/client-portal-api/utils/valuationChangeRequiresVerification';
import { RealEstateValuationInfoCard } from '@tcs/client-portal-common/components/templates/DetailCards/RealEstateValuationInfoCard';
import useFeature, {
  FeatureFlag,
} from '@tcs/client-portal-common/hooks/useFeature';
import useInvestmentValuationMutation from '@tcs/client-portal-common/hooks/useInvestmentValuationMutation';
import useIsUserRMD from '@tcs/client-portal-common/hooks/useIsUserRMD';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import { NotificationContext } from '@tcs/client-portal-common/providers/NotificationProvider';
import Checkbox from '@tcs/common/components/atoms/Checkbox';
import Form, {
  Field,
  FieldRenderProps,
  FormRenderProps,
  FormSpy,
} from '@tcs/common/components/atoms/Form';
import Radio from '@tcs/common/components/atoms/Radio';
import Spinner from '@tcs/common/components/atoms/Spinner';
import TextField from '@tcs/common/components/atoms/TextField';
import Dropzone from '@tcs/common/components/molecules/Dropzone';
import formatCurrency from '@tcs/common/utils/formatCurrency';
import unformatCurrency from '@tcs/common/utils/unformatCurrency';
import isWithinInterval from 'date-fns/isWithinInterval';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { array, boolean, mixed, object, string } from 'yup';
import { InfoGroup } from './InfoGroup';
import useStyles from './style';

interface ValuationFormProps {
  asset: AssetValuationDetails;
  accountNumber: string;
  onCancel: () => void;
  onSubmit: (status: 'Success' | 'Failure') => void;
}

interface DialogButtonProps {
  label: string;
  color: 'primary' | 'secondary';
  onClick: () => void;
  dataTestId: string;
}

interface DialogProps {
  title: string;
  message: string;
  buttons: DialogButtonProps[];
}

export const ValuationForm = ({
  onCancel,
  onSubmit,
  asset,
  accountNumber,
}: ValuationFormProps) => {
  const isMountedRef = useRef(false);
  const { clearNotification } = useContext(NotificationContext);

  // used to make sure we don't set the state after we have unmounted, see finally below
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const classes = useStyles();
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState<ValuationFormData>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const backdateValuationsAnytime = useFeature(
    FeatureFlag.BackdateValuationsAnytime,
  );
  const queryClient = useQueryClient();

  const currentYear = new Date().getFullYear();
  const shouldShowYear =
    backdateValuationsAnytime ||
    isWithinInterval(Date.now(), {
      start: new Date(currentYear, 0, 1),
      end: new Date(currentYear, 3, 15),
    });
  const isRMD = useIsUserRMD(accountNumber);
  const { oktaAuth } = useOktaAuth();
  const { profile } = useSession();
  const valuationMutator = useInvestmentValuationMutation(
    accountNumber,
    asset.id,
    oktaAuth,
  );
  const [showSpinner, setShowSpinner] = useState(false);

  const [isValueChangeBeExamined, setIsValueChangeBeExamined] = useState(false);
  const [areYouPlanningCheckboxChecked, setAreYouPlanningCheckboxChecked] =
    useState(false);

  const shouldValueChangeBeExamined = (current: string, previous: number) => {
    const currentNumber = unformatCurrency(`${current}`);
    const required = valuationChangeRequiresVerification(
      currentNumber,
      previous,
    );
    current !== '$' && setIsValueChangeBeExamined(required);
    return required;
  };

  const shouldRealEstateInfoBeDisplayed = (current: string) => {
    if (current === '') {
      return false;
    }
    const currentNumber = unformatCurrency(`${current}`);
    if (currentNumber === 0) {
      return false;
    }

    return true;
  };

  const validationSchema = useMemo(() => {
    return object({
      keepValue: boolean(),
      value: mixed()
        .required('This value is required')
        .test(
          'RealValue',
          'Please enter a dollar value of your asset',
          (val) =>
            (typeof val === 'number' && val > 0) ||
            (typeof val === 'string' && unformatCurrency(val) > 0),
        ),
      approve: boolean().isTrue(),
      files: array()
        .when(['isRMD'], {
          is: true,
          then: (schema) =>
            schema
              .min(1, 'Please upload a supporting document.')
              .required('Please upload a supporting document'),
        })
        .when('value', {
          is: (value) =>
            shouldValueChangeBeExamined(
              value,
              asset.assetType === 'RealEstate'
                ? asset.currentValue * (100 / asset.totalUnits)
                : asset.currentValue,
            ),
          then: (schema) =>
            schema
              .min(1, 'Please upload a supporting document.')
              .required('Please upload a supporting document'),
        })
        .when('planning', {
          is: true,
          then: (schema) =>
            schema
              .min(1, 'Please upload a supporting document.')
              .required('Please upload a supporting document'),
        }),
      year: string().required('Please specify year'),
    });
  }, [asset.currentValue]);

  const serializeValues = (values) => {
    const { value, ...rest } = values;
    return { ...rest, value: unformatCurrency(value) };
  };

  const handlePreSubmit = (values) => {
    setFormValues(serializeValues(values));
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    const data = formValues;
    const formData = new FormData();
    formData.append('valuation', data.value.toString());

    const isPriorYearValuation = data.year === 'prior';
    formData.append('isPriorYearValuation', isPriorYearValuation.toString());

    formData.append('email', profile.contactInfo.email);
    formData.append(
      'name',
      `${profile.personalInfo.firstName} ${profile.personalInfo.lastName}`,
    );
    if (
      data.files &&
      !isRMD &&
      (isValueChangeBeExamined || areYouPlanningCheckboxChecked)
    ) {
      data.files.map((file: Blob) => formData.append('files', file));
    }

    if (data.files && isRMD) {
      data.files.map((file: Blob) => formData.append('files', file));
    }

    // Append document reasons if any
    if (data.files && isRMD) {
      formData.append('documentReasons', 'Client account is in RMD status');
    }
    if (data.files && areYouPlanningCheckboxChecked) {
      formData.append(
        'documentReasons',
        'Client marked the valuation as related to a taxable event',
      );
    }
    if (data.files && isValueChangeBeExamined) {
      formData.append(
        'documentReasons',
        'Valuation is outside the allowed variance threshold',
      );
    }

    try {
      setIsSubmitting(true);
      setShowSpinner(true);
      await valuationMutator.mutateAsync(formData);
      setShowSpinner(false);
      setIsSubmitting(false);

      setIsSubmitted(true);
      clearNotification('valuation', accountNumber);
    } catch (e) {
      onSubmit('Failure');
      ToastUtils.error(
        `Failed to submit ${asset.name} valuation - please try again. If errors persist, contact support.`,
        true,
      );

      // we check if we are still mounted before running these so we don't get console errors
      // this is no longer needed in React 18 https://github.com/facebook/react/pull/22114
      if (isMountedRef.current) {
        setShowDialog(false);
        setShowSpinner(false);
      }
    }
  };

  const handleClose = async () => {
    setShowDialog(false);
    setFormValues(null);
    if (isSubmitted) {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.toString().startsWith('/valuations'),
      });
    }
  };

  const submitDialogContent = () =>
    dialogContent({
      title: 'Important',
      message:
        'Did you make sure all the information you provided is correct and up to date?',
      buttons: [
        {
          label: 'No',
          color: 'secondary',
          onClick: async () => await handleClose(),
          dataTestId: 'dialog-no-button',
        },
        {
          label: 'Yes',
          color: 'primary',
          onClick: handleSubmit,
          dataTestId: 'dialog-yes-button',
        },
      ],
    });

  const submitRealEstateDialogContent = () =>
    dialogContent({
      title: 'Confirm your action',
      message:
        "Please make sure you entered the full value of the property, even when you don't have 100% ownership.",
      buttons: [
        {
          label: 'Cancel',
          color: 'secondary',
          onClick: async () => await handleClose(),
          dataTestId: 'dialog-cancel-button',
        },
        {
          label: 'Confirm',
          color: 'primary',
          onClick: handleSubmit,
          dataTestId: 'dialog-confirm-button',
        },
      ],
    });

  const baseConfirmDialogContent = {
    title: 'Valuation Submitted',
    buttons: [
      {
        label: 'OK',
        color: 'primary' as const,
        onClick: async () => {
          await handleClose();
          onSubmit('Success');
        },
        dataTestId: 'dialog-ok-button',
      },
    ],
  };

  const confirmationDialogContent = () =>
    dialogContent({
      ...baseConfirmDialogContent,
      message:
        'Thank you for providing a current valuation. You should see the updated information online within the next 2 business days.',
    });

  const confirmationWithDocumentsDialogContent = () =>
    dialogContent({
      ...baseConfirmDialogContent,
      message:
        'Thank you for providing a current valuation. You should see the updated information online within the next 2 business days. \n\n A Forge Trust team member may reach out to request additional documentation if necessary to verify the current value provided.',
    });

  const dialogContent = (props: DialogProps) => (
    <Dialog open={!!showDialog} transitionDuration={0}>
      <Typography className={classes.dialogTitle} variant="h6">
        {isSubmitting ? 'Submitting' : props.title}
      </Typography>
      <DialogContent className={classes.dialogWrapper}>
        {showSpinner ? (
          // using the container and wrapper to make the popup keep the full max width
          <div className={classes.spinnerContainer}>
            <div className={classes.spinnerWrapper}>
              <Spinner />
            </div>
          </div>
        ) : (
          <>
            <Typography
              variant="body1"
              className={classes.dialogText}
              data-test-id="dialog-text">
              {props.message}
            </Typography>
            <div className={classes.dialogButtons}>
              <Grid container item spacing={2} justifyContent="flex-end">
                {props.buttons.map((button, index) => (
                  <Grid item key={index}>
                    <Button
                      onClick={button.onClick}
                      color={button.color}
                      data-test-id={button.dataTestId}>
                      {button.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  const dialog = isSubmitted
    ? formValues?.files?.length > 0
      ? confirmationWithDocumentsDialogContent()
      : confirmationDialogContent()
    : asset.assetType === 'RealEstate'
      ? submitRealEstateDialogContent()
      : submitDialogContent();

  const formatAssetValue = (v) => {
    if (!v) return v;
    const numberV = typeof v === 'number' ? v : v.replace(/[^0-9.-]/g, '');
    return formatCurrency(numberV);
  };

  const parseAssetValue = (v) => {
    if (!v) return v;
    const val = v.replace(/[^$,0-9.]/g, '');
    return isNaN(unformatCurrency(val)) ? '0' : val;
  };

  const prohibitedTransactionsURL =
    'https://forgetrust.com/prohibited-transactions/';

  const [
    isVisibleAreYouPlanningCheckboxAbove,
    setIsVisibleAreYouPlanningCheckboxAbove,
  ] = useState(false);
  const [
    isVisibleAreYouPlanningCheckboxBelow,
    setIsVisibleAreYouPlanningCheckboxBelow,
  ] = useState(true);
  const [isVisibleIsRMDMessageAbove, setIsVisibleIsRMDMessageAbove] =
    useState(false);
  const [isVisibleIsRMDMessageBelow, setIsVisibleIsRMDMessageBelow] =
    useState(false);
  const [
    isVisibleUploadSupportingDocuments,
    setIsVisibleUploadSupportingDocuments,
  ] = useState(false);
  const [isVisibleDropZone, setIsVisibleDropZone] = useState(false);

  const handleAreYouPlanningCheckBox = (e) => {
    setAreYouPlanningCheckboxChecked(e.target.checked);
  };

  const areYouPlanningCheckBox = () => {
    return (
      <FormControlLabel
        className={classes.checkbox}
        control={
          <Field
            name="planning"
            component={Checkbox}
            type="checkbox"
            onClick={(e) => {
              handleAreYouPlanningCheckBox(e);
            }}
          />
        }
        label={
          <div>
            <span>
              Check this box if you are planning to take an in-kind distribution
              of this investment or include it in a conversion or
              recharacterization request in the next calendar year.
            </span>
          </div>
        }
        onChange={handleAreYouPlanningCheckBox}
      />
    );
  };

  const infoGroupIsRMD = () => {
    return (
      <div className={classes.infoGroup}>
        <InfoGroup isRMD={isRMD} assetType={asset.assetType} />
      </div>
    );
  };

  const handleValueFieldOnChange = (e) => {
    if (e.target.value === '') return;

    // compare the newly entered value against the current value
    // be sure to use total property value for real estate
    let assetValue = asset.currentValue;
    if (asset.assetType === 'RealEstate') {
      assetValue = asset.currentValue * (100 / asset.totalUnits);
    }

    const shouldBeExamined = shouldValueChangeBeExamined(
      e.target.value,
      assetValue,
    );

    setIsValueChangeBeExamined(shouldBeExamined);
  };

  useEffect(() => {
    if (isRMD) {
      setIsVisibleAreYouPlanningCheckboxAbove(false);
      setIsVisibleAreYouPlanningCheckboxBelow(true);
      setIsVisibleIsRMDMessageAbove(true);
      setIsVisibleIsRMDMessageBelow(false);
      setIsVisibleUploadSupportingDocuments(true);
      setIsVisibleDropZone(true);
    } else {
      if (
        (isValueChangeBeExamined && !areYouPlanningCheckboxChecked) ||
        (isValueChangeBeExamined && areYouPlanningCheckboxChecked)
      ) {
        setIsVisibleAreYouPlanningCheckboxAbove(false);
        setIsVisibleAreYouPlanningCheckboxBelow(true);
        setIsVisibleIsRMDMessageAbove(true);
        setIsVisibleIsRMDMessageBelow(false);
        setIsVisibleUploadSupportingDocuments(true);
        setIsVisibleDropZone(true);
        return;
      }

      if (!isValueChangeBeExamined && !areYouPlanningCheckboxChecked) {
        setIsVisibleAreYouPlanningCheckboxAbove(false);
        setIsVisibleAreYouPlanningCheckboxBelow(true);
        setIsVisibleIsRMDMessageAbove(false);
        setIsVisibleIsRMDMessageBelow(false);
        setIsVisibleUploadSupportingDocuments(false);
        setIsVisibleDropZone(false);
      }

      if (!isValueChangeBeExamined && areYouPlanningCheckboxChecked) {
        setIsVisibleAreYouPlanningCheckboxAbove(true);
        setIsVisibleAreYouPlanningCheckboxBelow(false);
        setIsVisibleIsRMDMessageAbove(false);
        setIsVisibleIsRMDMessageBelow(true);
        setIsVisibleDropZone(true);
        return;
      }
    }
  }, [areYouPlanningCheckboxChecked, isValueChangeBeExamined]);

  return (
    <>
      <Form
        onSubmit={handlePreSubmit}
        initialValues={{
          keepValue: false,
          approve: false,
          planning: false,
          year: 'current',
          isRMD,
          value: '$',
        }}
        validationSchema={validationSchema}
        render={({ handleSubmit, submitting, values }: FormRenderProps) => (
          <form onSubmit={handleSubmit} data-test-id="update-valuation-form">
            <FormSpy
              subscription={{ values: true, errors: true }}
              onChange={({ values, errors }) => {
                let assetValue = asset.currentValue;
                if (asset.assetType === 'RealEstate') {
                  assetValue = asset.currentValue * (100 / asset.totalUnits);
                }

                if (
                  values.keepValue &&
                  values.value !== formatCurrency(assetValue)
                ) {
                  values.value = formatCurrency(assetValue);
                  errors.value = '';
                  // Error field is not rerendered automatically
                  setRefreshKey((val) => val + 1);
                }
              }}
            />
            <Grid container direction="column">
              <Grid item className={classes.mainForm}>
                <Grid container direction="column" spacing={3}>
                  <Grid item>
                    <Typography variant="h6">
                      New Valuation of the Asset
                    </Typography>
                    {asset.assetType !== 'RealEstate' && (
                      <Banner severity="warning" bannerType="inline">
                        {`Please don't include Custodial Cash held directly with Forge Trust Co.`}
                      </Banner>
                    )}
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      className={classes.checkbox}
                      control={
                        <Field
                          name="keepValue"
                          component={Checkbox}
                          type="checkbox"
                        />
                      }
                      label="Please check this box if there is no change to the value"
                    />
                    <Field
                      name="value"
                      format={formatAssetValue}
                      formatOnBlur
                      parse={parseAssetValue}
                      render={(props: FieldRenderProps<string>) => (
                        <>
                          <TextField
                            data-private
                            disabled={values.keepValue}
                            key={refreshKey}
                            label="New Market Value"
                            name="value"
                            {...props}
                            onChange={(e) => {
                              props.input.onChange(e);
                              handleValueFieldOnChange(e);
                            }}
                          />
                          {asset.assetType === 'RealEstate' &&
                            shouldRealEstateInfoBeDisplayed(values.value) && (
                              <RealEstateValuationInfoCard
                                percentageShare={asset.totalUnits}
                                totalValue={unformatCurrency(values.value)}
                              />
                            )}
                        </>
                      )}
                    />
                  </Grid>
                  {shouldShowYear && (
                    <Grid item>
                      <FormLabel component="legend">Value as of date</FormLabel>
                      <RadioGroup className={classes.asOf}>
                        <FormControlLabel
                          label="Current year"
                          control={
                            <Field
                              name="year"
                              component={Radio}
                              type="radio"
                              value="current"
                            />
                          }
                        />
                        <FormControlLabel
                          label="Last year"
                          control={
                            <Field
                              name="year"
                              component={Radio}
                              type="radio"
                              value="prior"
                            />
                          }
                        />
                      </RadioGroup>
                    </Grid>
                  )}
                  {isVisibleUploadSupportingDocuments && (
                    <Grid item>
                      <FormLabel
                        component="legend"
                        className={classes.formLabel}>
                        Upload supporting documents (
                        {isRMD ||
                        shouldValueChangeBeExamined(
                          values.value,
                          asset.assetType === 'RealEstate'
                            ? asset.currentValue * (100 / asset.totalUnits)
                            : asset.currentValue,
                        )
                          ? 'required'
                          : 'optional'}
                        )
                      </FormLabel>
                    </Grid>
                  )}
                  {isVisibleIsRMDMessageAbove && (
                    <Grid item>{infoGroupIsRMD()}</Grid>
                  )}
                  {isVisibleAreYouPlanningCheckboxAbove && (
                    <Grid item>{areYouPlanningCheckBox()}</Grid>
                  )}
                  {isVisibleDropZone && (
                    <Grid item>
                      <Field
                        name="files"
                        render={(props: FieldRenderProps<string>) => (
                          <Dropzone {...props} accountNumber={accountNumber} />
                        )}
                      />
                    </Grid>
                  )}
                  {isVisibleIsRMDMessageBelow && (
                    <Grid item>{infoGroupIsRMD()}</Grid>
                  )}

                  {isVisibleAreYouPlanningCheckboxBelow && (
                    <Grid item>{areYouPlanningCheckBox()}</Grid>
                  )}
                  <Grid item>
                    <FormControlLabel
                      className={classes.checkbox}
                      control={
                        <Field
                          name="approve"
                          component={Checkbox}
                          type="checkbox"
                        />
                      }
                      label={
                        <div>
                          <span>
                            By checking this box, I certify under penalty of
                            perjury that I am the account holder, and that the
                            above information is true, correct and represents
                            the fair market value of the asset(s) named above. I
                            further certify that I know and understand the rules
                            and regulations of the Internal Revenue Service and
                            Department of Labor regarding&nbsp;
                          </span>
                          <Link
                            href={prohibitedTransactionsURL}
                            data-test-id="prohibited-transactions-button"
                            target="_blank">
                            prohibited transactions
                          </Link>
                          <span>
                            &nbsp;within a retirement account and that I have
                            not engaged in any such transactions. I confirm that
                            the custodial account assets do not include any
                            illegal or impermissible investments under South
                            Dakota or Federal law, including, but not limited
                            to, holdings of marijuana or illegal substances,
                            illegal gambling, or illegal artifacts, and should
                            the custodial account ever come to hold an illegal
                            or impermissible investment under South Dakota or
                            federal law, custodial account holder will notify
                            Forge Trust immediately.
                          </span>
                        </div>
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item spacing={2} justifyContent="flex-end">
                <Grid item>
                  <Button
                    size="medium"
                    color="secondary"
                    onClick={onCancel}
                    data-test-id="cancel-button">
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="medium"
                    color="primary"
                    type="submit"
                    disabled={submitting}
                    data-test-id="next-button">
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </form>
        )}
      />
      {dialog}
    </>
  );
};

export default ValuationForm;
